import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { Organization } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { profileFormFor, toUpdateOrganizationRequest } from '../api/mappers';
import { useUpdateOrganization } from '../hooks/use-update-organization';
import { profileSchema, type ProfileValues } from '../schemas/profile-schema';

export interface EditOrganizationDialogProps {
  organization: Organization;
}

/** Name, billing email and the operators' private notes. The slug is permanent. */
export function EditOrganizationDialog({ organization }: EditOrganizationDialogProps) {
  const { t } = useTranslation(['organizations', 'common']);
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-tour="platform.organizations.edit">
          <Pencil aria-hidden />
          {t('organizations:edit.open')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('organizations:edit.title')}</DialogTitle>
          <DialogDescription>{t('organizations:edit.description')}</DialogDescription>
        </DialogHeader>
        {open ? <EditForm organization={organization} onDone={() => setOpen(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function EditForm({ organization, onDone }: { organization: Organization; onDone: () => void }) {
  const { t } = useTranslation(['organizations', 'common']);
  const update = useUpdateOrganization(organization.id);
  const form = useForm<ProfileValues>({
    resolver: useI18nResolver(profileSchema),
    defaultValues: profileFormFor(organization),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    const body = toUpdateOrganizationRequest(values, organization);
    if (Object.keys(body).length === 0) {
      onDone();
      return;
    }
    try {
      await update.mutateAsync(body);
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('organizations:fields.name')}</FormLabel>
              <FormControl>
                <Input autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="billingEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('organizations:fields.billingEmail')}</FormLabel>
              <FormControl>
                <Input type="email" autoComplete="off" {...field} />
              </FormControl>
              <FormDescription>{t('organizations:edit.billingEmailHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('organizations:fields.notes')}</FormLabel>
              <FormControl>
                <Textarea rows={5} {...field} />
              </FormControl>
              <FormDescription>{t('organizations:fields.notesHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
